import React, { useEffect, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import ReactGA from 'react-ga4';
import { ADS_CONVERSION_ID, trackAdsConversion } from './utils/analytics';
import lazyWithRetry from './utils/lazyWithRetry';

// Eagerly loaded (always visible)
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import WhatsAppIcon from './components/WhatsAppIcon';
import TranslateWidget from './components/TranslateWidget';
import Schedulewedget from './components/Schedulewedget';
import ErrorBoundary from './components/ErrorBoundary';
import Breadcrumbs from './components/Breadcrumbs';
import CookieConsent from './components/CookieConsent';
import LoadingSpinner from './components/LoadingSpinner';
import MobileStickyCTA from './components/MobileStickyCTA';
//import WildlifeEncounters from './components/WildlifeEncounters';

// Lazy loaded routes (code-split). lazyWithRetry reloads once when a chunk
// disappears after a redeploy instead of surfacing the error boundary.
const Hero = lazyWithRetry(() => import('./components/Hero'));
const Services = lazyWithRetry(() => import('./components/Services'));
const Transport = lazyWithRetry(() => import('./components/Transport'));
const Hotels = lazyWithRetry(() => import('./components/Hotels'));
const Contact = lazyWithRetry(() => import('./components/Contact'));
const ThankYou = lazyWithRetry(() => import('./components/ThankYou'));
const GuiderPage = lazyWithRetry(() => import('./components/Guide'));
const BirdingTours = lazyWithRetry(() => import('./components/BirdTour'));
const PoasVolcano = lazyWithRetry(() => import('./components/detailTours/PoasVolcano'));
const PoasDoka = lazyWithRetry(() => import('./components/detailTours/PoasDoka'));
const PoasMirador = lazyWithRetry(() => import('./components/detailTours/PoasMirador'));
const Doka = lazyWithRetry(() => import('./components/detailTours/Doka'));
const Sarchi = lazyWithRetry(() => import('./components/detailTours/Sarchi'));
const Waterfall = lazyWithRetry(() => import('./components/detailTours/Waterfall'));
const Mirador = lazyWithRetry(() => import('./components/detailTours/Mirador'));
const NotFound = lazyWithRetry(() => import('./components/NotFound'));
const WildlifeDirectory = lazyWithRetry(() => import('./components/WildlifeDirectory'));
const BirdsAnthology = lazyWithRetry(() => import('./components/BirdsAnthology'));
const TourPage = lazyWithRetry(() => import('./components/tours/TourPage'));
const SampleItinerary = lazyWithRetry(() => import('./components/SampleItinerary'));
const PartnerLodge = lazyWithRetry(() => import('./components/PartnerLodge'));
//const WildlifeDirectory1 = lazy(() => import('./components/WildlifeDirectory1'));

// Initialize Google Analytics + Google Ads (respects cookie consent).
// Both IDs route through the same gtag.js instance, so Ads conversions can
// fire via window.gtag('event', 'conversion', { send_to: 'AW-...' }).
const consent = localStorage.getItem('cookie_consent');
if (consent !== 'declined') {
  ReactGA.initialize([
    { trackingId: 'G-VQ2280K3B8' },
    { trackingId: ADS_CONVERSION_ID },
  ]);
}

// Page view tracking component
const TrackPageViews = () => {
  const location = useLocation();

  useEffect(() => {
    if (consent !== 'declined') {
      ReactGA.send({
        hitType: 'pageview',
        page: location.pathname + location.search,
      });
    }
  }, [location]);

  return null;
};

// Document-level listener that fires Ads conversions for tel: and WhatsApp
// clicks anywhere in the app — covers Footer, Hero, Guide, the floating
// WhatsApp icon, and any future link without per-component wiring.
const TrackAdsClicks = () => {
  useEffect(() => {
    if (consent === 'declined') return undefined;
    const handler = (e) => {
      const link = e.target.closest && e.target.closest('a');
      if (!link) return;
      const href = link.getAttribute('href') || '';
      if (href.startsWith('tel:')) {
        trackAdsConversion('phoneClick');
      } else if (href.includes('wa.me/') || href.startsWith('https://api.whatsapp.com')) {
        trackAdsConversion('whatsappClick');
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);
  return null;
};

// ErrorBoundary resets when the route changes, so one crashed page never
// dead-ends the whole session.
const RouteAwareErrorBoundary = ({ children }) => {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
};

const App = () => {
  return (
    <HelmetProvider>
      <Router>
        <RouteAwareErrorBoundary>
          <TrackPageViews />
          <TrackAdsClicks />

          {/* Skip to main content - accessibility */}
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:bg-green-600 focus:text-white focus:px-4 focus:py-2 focus:rounded"
          >
            Skip to main content
          </a>

          <Navbar />
          <WhatsAppIcon />
          <TranslateWidget />
         {/* <WildlifeEncounters />*/}
          <Breadcrumbs />

          <main id="main-content">
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/" element={<Hero />} />
                <Route path="/transport" element={<Transport />} />
                <Route path="/hotels" element={<Hotels />} />
                <Route
                  path="/services"
                  element={
                    <>
                      <Services />
                      <Schedulewedget />
                    </>
                  }
                />
                <Route path="/contact" element={<Contact />} />
                <Route path="/thank-you" element={<ThankYou />} />
                <Route
                  path="/poasvolcano"
                  element={
                    <>
                      <PoasVolcano />
                      <Schedulewedget />
                    </>
                  }
                />
                <Route
                  path="/poasdoka"
                  element={
                    <>
                      <PoasDoka />
                      <Schedulewedget />
                    </>
                  }
                />
                <Route
                  path="/poasmirador"
                  element={
                    <>
                      <PoasMirador />
                      <Schedulewedget />
                    </>
                  }
                />
                <Route
                  path="/doka"
                  element={
                    <>
                      <Doka />
                      <Schedulewedget />
                    </>
                  }
                />
                <Route
                  path="/sarchi"
                  element={
                    <>
                      <Sarchi />
                      <Schedulewedget />
                    </>
                  }
                />
                <Route
                  path="/waterfall"
                  element={
                    <>
                      <Waterfall />
                      <Schedulewedget />
                    </>
                  }
                />
                <Route
                  path="/mirador"
                  element={
                    <>
                      <Mirador />
                      <Schedulewedget />
                    </>
                  }
                />
                <Route path="/birding" element={<BirdingTours />} />
                <Route path="/itinerary" element={<SampleItinerary />} />
                <Route path="/lodge" element={<PartnerLodge />} />
                <Route path="/wildlife" element={<BirdsAnthology defaultCategory={null} />} />
                <Route path="/wildlife/:speciesId" element={<BirdsAnthology defaultCategory={null} />} />
                <Route path="/wildlife-classic" element={<WildlifeDirectory />} />
                <Route path="/birds" element={<BirdsAnthology defaultCategory="bird" />} />
                {/* <Route path="/wildlife1" element={<WildlifeDirectory1 />} /> */}
                <Route path="/guide" element={<GuiderPage />} />
                <Route path="/tours/:slug" element={<TourPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>

          <Footer />
          <CookieConsent />
          <MobileStickyCTA />
        </RouteAwareErrorBoundary>
      </Router>
    </HelmetProvider>
  );
};

export default App;
